import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useAuth } from './useAuth';

interface JobPayment {
  id: string;
  job_id: string;
  amount: number;
  payment_method: string;
  payment_date: string;
  notes?: string;
  recorded_by: string;
  created_at: string;
}

export const jobPaymentsQueryKey = (jobId: string | null) => ['job-payments', jobId] as const;

export async function fetchJobPayments(jobId: string | null): Promise<JobPayment[]> {
  if (!jobId) return [];

  const { data, error } = await supabase
    .from('job_payments')
    .select('*')
    .eq('job_id', jobId)
    .order('payment_date', { ascending: false });

  if (error) throw error;
  return data as JobPayment[];
}

export function useJobPayments(jobId: string | null) {
  return useQuery({
    queryKey: jobPaymentsQueryKey(jobId),
    queryFn: () => fetchJobPayments(jobId),
    enabled: !!jobId,
  });
}

export function useAddJobPayment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (paymentData: {
      job_id: string;
      amount: number;
      payment_method: string;
      payment_date: string;
      notes?: string;
    }) => {
      const { error } = await supabase.from('job_payments').insert([{
        ...paymentData,
        recorded_by: user?.id,
      } as any]);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['job-payments', variables.job_id] });
      queryClient.invalidateQueries({ queryKey: ['job', variables.job_id] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      toast({ title: 'Payment recorded successfully' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to record payment',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateJobPayment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      paymentId,
      jobId,
      updates,
      reason,
    }: {
      paymentId: string;
      jobId: string;
      updates: {
        amount?: number;
        payment_method?: string;
        payment_date?: string;
        notes?: string | null;
      };
      reason: string;
    }) => {
      const { data: before, error: fetchError } = await supabase
        .from('job_payments')
        .select('*')
        .eq('id', paymentId)
        .single();
      if (fetchError) throw fetchError;
      if (!before) throw new Error('Payment not found');

      const { error: updateError } = await supabase
        .from('job_payments')
        .update(updates)
        .eq('id', paymentId);
      if (updateError) throw updateError;

      const { error: auditError } = await supabase
        .from('audit_logs')
        .insert({
          action: 'payment_updated',
          entity: 'job_payment',
          entity_id: paymentId,
          actor_user_id: user?.id,
          company_id: before.company_id,
          metadata: {
            before: {
              amount: before.amount,
              payment_method: before.payment_method,
              payment_date: before.payment_date,
              notes: before.notes,
            },
            after: updates,
            edit_reason: reason,
            job_id: jobId,
            edited_at: new Date().toISOString(),
          },
        });

      if (auditError) console.error('Failed to create audit log:', auditError);

      return jobId;
    },
    onSuccess: (jobId) => {
      queryClient.invalidateQueries({ queryKey: ['job-payments', jobId] });
      queryClient.invalidateQueries({ queryKey: ['job', jobId] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      toast({ title: 'Payment updated successfully' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update payment',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteJobPayment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ 
      paymentId, 
      jobId, 
      reason 
    }: { 
      paymentId: string; 
      jobId: string; 
      reason: string;
    }) => {
      // First, fetch the payment details before deletion
      const { data: payment, error: fetchError } = await supabase
        .from('job_payments')
        .select('*')
        .eq('id', paymentId)
        .single();

      if (fetchError) throw fetchError;
      if (!payment) throw new Error('Payment not found');

      // Delete the payment
      const { error: deleteError } = await supabase
        .from('job_payments')
        .delete()
        .eq('id', paymentId);

      if (deleteError) throw deleteError;

      // Insert audit log
      const { error: auditError } = await supabase
        .from('audit_logs')
        .insert({
          action: 'payment_deleted',
          entity: 'job_payment',
          entity_id: paymentId,
          actor_user_id: user?.id,
          company_id: payment.company_id,
          metadata: {
            amount: payment.amount,
            payment_method: payment.payment_method,
            payment_date: payment.payment_date,
            original_notes: payment.notes,
            recorded_by: payment.recorded_by,
            deletion_reason: reason,
            job_id: jobId,
            deleted_at: new Date().toISOString(),
          },
        });

      if (auditError) {
        console.error('Failed to create audit log:', auditError);
        // Don't throw - payment is already deleted
      }

      return jobId;
    },
    onSuccess: (jobId) => {
      queryClient.invalidateQueries({ queryKey: ['job-payments', jobId] });
      queryClient.invalidateQueries({ queryKey: ['job', jobId] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      toast({ title: 'Payment deleted and logged successfully' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to delete payment',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
